/**
 * Registro de correos (Fase 1 de retiros, 2026-10-29): cada correo que salió del sistema hacia el cliente, en la sección
 * «Correos» del detalle de la venta y de la ficha del cliente. Dice la fecha, a quién, quién lo envió (o «Automático»),
 * si salió o falló (con el motivo) y qué decía: el asunto y un resumen (en el estado del retiro, lo comprado, lo
 * retirado y lo pendiente por producto). Nunca el correo completo.
 */
import { formatearCantidad, Money, TIPO_CORREO_ETIQUETA, type CorreoRegistroDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { EstadoQuery } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { get } from '@/lib/api.ts';
import { formatearFechaHora } from '@/lib/utils.ts';

export const clavesRegistroCorreos = (q: { documento_id?: string; cliente_id?: string }) => ['correo', 'registro', q] as const;

interface LineaRetiro {
  codigo: string;
  producto: string;
  unidad: string;
  comprado: string;
  retirado: string;
  pendiente: string;
}

const cant = (v: string) => formatearCantidad(Money.parse(v));

/** Lo que decía el correo, a partir del resumen guardado. */
function Contenido({ c }: { c: CorreoRegistroDTO }) {
  const lineas = Array.isArray(c.contenido['lineas']) ? (c.contenido['lineas'] as LineaRetiro[]) : null;
  const motivo = typeof c.contenido['motivo'] === 'string' ? c.contenido['motivo'] : null;
  const detalle = typeof c.contenido['detalle'] === 'string' ? c.contenido['detalle'] : null;
  const renglones = typeof c.contenido['renglones'] === 'number' ? c.contenido['renglones'] : null;
  return (
    <div className="space-y-1 text-xs" data-testid="correo-contenido">
      <p className="font-medium">{c.asunto}</p>
      {motivo ? <p className="text-muted-foreground">{motivo}{detalle ? ` · ${detalle}` : ''}</p> : null}
      {renglones !== null ? <p className="text-muted-foreground">{renglones} renglón(es){c.adjuntos.length > 0 ? ` · adjuntos: ${c.adjuntos.join(', ')}` : ''}</p> : null}
      {lineas ? (
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-muted-foreground"><th className="text-left font-normal">Producto</th><th className="text-right font-normal">Comprado</th><th className="text-right font-normal">Retirado</th><th className="text-right font-normal">Pendiente</th></tr>
          </thead>
          <tbody>
            {lineas.map((l) => (
              <tr key={l.codigo}>
                <td>{l.producto}</td>
                <td className="text-right num">{cant(l.comprado)} {l.unidad}</td>
                <td className="text-right num">{cant(l.retirado)}</td>
                <td className="text-right num font-semibold">{cant(l.pendiente)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : null}
    </div>
  );
}

interface Props {
  documentoId?: string;
  clienteId?: string;
  /** En la ficha del cliente se dice de qué documento es cada correo. */
  conReferencia?: boolean;
}

export function CorreosEnviados({ documentoId, clienteId, conReferencia = false }: Props) {
  const q = documentoId ? { documento_id: documentoId } : { cliente_id: clienteId ?? '' };
  const lista = useQuery({ queryKey: clavesRegistroCorreos(q), queryFn: () => get<CorreoRegistroDTO[]>('/correo/registro', q), enabled: !!(documentoId || clienteId) });
  const [abierto, setAbierto] = useState<string | null>(null);
  return (
    <div data-testid="correos-enviados">
      <p className="mb-2 flex items-center gap-2 text-sm font-semibold"><Mail className="h-4 w-4 text-mg-verde" /> Correos</p>
      {/* Una respuesta vacía (null) cuenta como «sin correos»: no rompe el detalle del documento */}
      <EstadoQuery cargando={lista.isLoading} error={lista.error} datos={lista.data ?? (lista.isLoading ? undefined : [])} reintentar={() => void lista.refetch()}>
        {(correos) =>
          correos.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="correos-vacio">Todavía no se le ha enviado ningún correo.</p>
          ) : (
            <ul className="divide-y text-sm">
              {correos.map((c) => (
                <li key={c.id} className="py-2" data-testid={`correo-${c.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium">
                        {TIPO_CORREO_ETIQUETA[c.tipo] ?? c.tipo}
                        {conReferencia && c.referencia && c.tipo !== 'documento' ? <span className="font-normal text-muted-foreground"> · {c.referencia}</span> : null}
                        {conReferencia && c.tipo === 'documento' && c.referencia ? <span className="font-normal"> · {c.referencia}</span> : null}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {formatearFechaHora(c.enviado_en)} · a {c.para} · {c.automatico ? 'Automático' : `por ${c.usuario_nombre ?? '—'}`}{c.sucursal_nombre ? ` · ${c.sucursal_nombre}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {c.estado === 'enviado' ? <Badge variant="ok" data-testid="correo-estado">Enviado</Badge> : <Badge variant="critico" data-testid="correo-estado">No salió</Badge>}
                      <Button size="sm" variant="ghost" onClick={() => setAbierto(abierto === c.id ? null : c.id)} data-testid="correo-ver">{abierto === c.id ? 'Ocultar' : 'Qué decía'}</Button>
                    </div>
                  </div>
                  {c.estado === 'fallido' && c.error ? <p className="mt-1 text-xs text-mg-rojo" data-testid="correo-error">Motivo: {c.error}</p> : null}
                  {abierto === c.id ? <div className="mt-2 rounded-lg bg-muted/50 p-2"><Contenido c={c} /></div> : null}
                </li>
              ))}
            </ul>
          )
        }
      </EstadoQuery>
    </div>
  );
}
